import { useState } from "react";
import { FileDown, FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, DataTable, Sel, BranchSel, Spinner } from "@/components/kit";
import { useApi, download, today, peso } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MONEY = /(gross|net|revenue|amount|refunds|voids)/i;

export default function Reports() {
  const { can, branch, setBranch } = useAuth();
  const d30 = new Date(Date.now() - 29 * 864e5).toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
  const [f, setF] = useState({ date_from: d30, date_to: today() });
  const list = useApi("/reports", {});
  const avail = (list.data || []).filter((r) => !r.financial || can("view_financial"));
  const [rtype, setRtype] = useState("daily_revenue");
  const key = avail.find((r) => r.key === rtype) ? rtype : avail[0]?.key;
  const { data, loading } = useApi(key ? `/reports/${key}` : null, { ...f, branch_id: branch });
  const params = { ...f, branch_id: branch };
  const cols = (data?.columns || []).map((c, i) => ({ key: String(i), label: c, render: (r) => (MONEY.test(c) && typeof r[i] === "number" ? <span className="font-mono">{peso(r[i])}</span> : r[i] ?? "—") }));
  return (
    <div className="fade-up">
      <PageHeader title="Reports" subtitle={data?.title} testid="reports-header">
        {can("export") && key && (
          <>
            <Button variant="outline" size="sm" data-testid="export-csv" onClick={() => download(`/reports/${key}`, { ...params, format: "csv" }, `${key}.csv`)}><FileDown className="h-4 w-4" /> CSV</Button>
            <Button variant="outline" size="sm" data-testid="export-xlsx" onClick={() => download(`/reports/${key}`, { ...params, format: "xlsx" }, `${key}.xlsx`)}><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
            <Button variant="outline" size="sm" data-testid="export-pdf" onClick={() => download(`/reports/${key}`, { ...params, format: "pdf" }, `${key}.pdf`)}><FileText className="h-4 w-4" /> PDF</Button>
          </>
        )}
      </PageHeader>
      <div className="mb-4 flex flex-wrap gap-2">
        <Sel value={key} onChange={setRtype} testid="report-type" className="w-full sm:w-64" options={avail.map((r) => ({ value: r.key, label: r.name }))} />
        <Input type="date" data-testid="report-from" className="w-40" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <Input type="date" data-testid="report-to" className="w-40" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
        <BranchSel value={branch} onChange={setBranch} testid="report-branch" />
      </div>
      {!data && loading ? <Spinner /> : <DataTable testid="report-table" loading={loading} rows={(data?.rows || []).map((r, i) => Object.assign([...r], { id: i }))} columns={cols} />}
    </div>
  );
}
